import { Injectable, effect, signal, Inject, PLATFORM_ID, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';

import { environment } from 'src/environments/environment';

export type MenuMode =
  | 'static'
  | 'overlay'
  | 'horizontal'
  | 'slim'
  | 'slim-plus'
  | 'reveal'
  | 'drawer';

export type ColorScheme = 'light' | 'dark';

const configName = 'config';

// Cut from V19
//
//export interface layoutConfig {
//  primary: string;
//  surface: string | undefined | null;
//  darkTheme: boolean;
//  menuMode: MenuMode;
//  menuTheme: string;
//  topbarTheme: string;
//  menuProfilePosition: string;
//}
export interface AppConfig {
  inputStyle: string;
  colorScheme: ColorScheme;
  componentTheme: string;
  ripple: boolean;
  menuMode: MenuMode;
  scale: number;
  menuTheme: string;
  topbarTheme: string;
  menuProfilePosition: string;
}

interface LayoutState {
  staticMenuMobileActive: boolean;
  overlayMenuActive: boolean;
  staticMenuDesktopInactive: boolean;
  configSidebarVisible: boolean;
  menuHoverActive: boolean;
  rightMenuActive: boolean;
  topbarMenuActive: boolean;
  menuProfileActive: boolean;
  sidebarActive: boolean;
  anchored: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  _config: AppConfig = {
    ripple: true,
    inputStyle: 'outlined',
    menuMode: 'slim',
    colorScheme: 'light',
    componentTheme: 'indigo',
    scale: 12,
    menuTheme: 'light',
    topbarTheme: 'indigo',
    menuProfilePosition: 'end',
  };

  state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    menuProfileActive: false,
    sidebarActive: false,
    anchored: false,
  };

  // Cut from V19
  //
  _state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    sidebarActive: false,
    anchored: false,
    // activeMenuItem: null,
    // overlaySubmenuActive: false,
    menuProfileActive: false
  };
  //layoutConfig = signal<layoutConfig>(this._config);
  //layoutState = signal<LayoutState>(this._state);

  config = signal<AppConfig>(this._config);

  private initialized = false;

  private configUpdate = new Subject<AppConfig>();

  private overlayOpen = new Subject<any>();

  private topbarMenuOpen = new Subject<any>();

  private menuProfileOpen = new Subject<any>();

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  topbarMenuOpen$ = this.topbarMenuOpen.asObservable();

  menuProfileOpen$ = this.menuProfileOpen.asObservable();

  private saveConfig(): void {
    console.log('*** LayoutService saveConfig');
    if (isPlatformBrowser(this.platformId)) {

      environment.production || console.log('*** LayoutService Saving config to localStorage: ', JSON.stringify(this.config()));

      localStorage.setItem(configName, JSON.stringify(this.config()));
    }
  }

  public getInitialConfig(): AppConfig {
    if (isPlatformBrowser(this.platformId)) {
      const savedConfig = localStorage.getItem(configName);
      if (savedConfig) {
        try {
          const parsedConfig = JSON.parse(savedConfig);
          environment.production || console.log('*** LayoutService getInitialConfig parsedConfig: ', JSON.stringify(parsedConfig));

          // Merge the loaded config with defaults to ensure all properties are present
          return { ...this._config, ...parsedConfig };
        } catch (e) {
          console.error('Could not parse app-config from localStorage', e);
        }
      }
    }
    // Return the default config if not in a browser or if loading fails
    return this._config;
  }

  onConfigUpdate() {
    const config = this.config();
    this._config = { ...config };

    environment.production || console.log('*** LayoutService onConfigUpdate: ', JSON.stringify(config));
    environment.production || console.log('*** LayoutService onConfigUpdate: ', JSON.stringify(this._config));

    this.saveConfig();
    this.configUpdate.next(this.config());
  }

  //constructor(@Inject(PLATFORM_ID) private platformId: object) {
  //  environment.production || console.log('*** LayoutService constructor: ', JSON.stringify(this.config()));

  //  effect(() => {
  //    // Read the config signal to create a dependency
  //    const newConfig = this.config();
  //    environment.production || console.log('*** LayoutService constructor 1st effect: ', JSON.stringify(newConfig));
  //    environment.production || console.log('*** LayoutService constructor 1st effect: ', untracked(() => this.initialized));

  //    // On the first run, this.initialized is false, so we skip.
  //    // We use untracked() to read `this.initialized` without creating a dependency on it.
  //    if (!untracked(() => this.initialized)) {

  //      this.initialized = true;
  //      const initialConfig = this.getInitialConfig();

  //      environment.production || console.log('*** LayoutService constructor 3rd effect: ', untracked(() => this.initialized));
  //      environment.production || console.log('*** LayoutService constructor 3rd initialConfig: ', JSON.stringify(initialConfig));
  //      environment.production || console.log('*** LayoutService constructor 3rd _config: ', JSON.stringify(this._config));

  //      if (this.updateStyle(initialConfig)) {
  //        this._config = initialConfig;
  //        this.changeTheme();
  //      }
  //      this.changeScale(initialConfig.scale);

  //      // Update the signal with the loaded configuration
  //      this.config.set(initialConfig);
  //      return;
  //    }
  //    environment.production || console.log('*** LayoutService constructor 2nd effect: ', JSON.stringify(newConfig));
  //    environment.production || console.log('*** LayoutService constructor 2nd effect: ', untracked(() => this.initialized));

  //    if (isPlatformBrowser(this.platformId)) {
  //      // For all subsequent runs, apply the changes.
  //      if (this.updateStyle(newConfig)) {
  //        this.changeTheme();
  //      }
  //      this.changeScale(newConfig.scale);
  //      this.onConfigUpdate();
  //    }
  //  });

  //  // Apply initial theme and scale directly
  //  environment.production || console.log('*** LayoutService on exit constructor: ', JSON.stringify(this.config()));
  //}

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    // Load and apply initial config BEFORE setting up the effect
    const initialConfig = this.getInitialConfig();
    this._config = initialConfig;
    this.config.set(initialConfig);

    // Apply initial theme and scale
    this.changeTheme();
    this.changeScale(initialConfig.scale);

    environment.production || console.log('*** LayoutService constructor: ', JSON.stringify(this.config()));

    // Set up effect to handle FUTURE changes only
    effect(() => {
      const newConfig = this.config();
      environment.production || console.log('*** LayoutService effect triggered: ', JSON.stringify(newConfig));

      // Skip the first run (initialization already done above)
      if (!untracked(() => this.initialized)) {
        this.initialized = true;
        return;
      }

      environment.production || console.log('*** LayoutService applying config change: ', JSON.stringify(newConfig));

      if (isPlatformBrowser(this.platformId)) {
        // For all subsequent runs, apply the changes.
        if (this.updateStyle(newConfig)) {
          this._config = { ...newConfig };
          this.changeTheme();
        }
        this.changeScale(newConfig.scale);
        this.onConfigUpdate();
      }
    });

    environment.production || console.log('*** LayoutService on exit constructor: ', JSON.stringify(this.config()));
  } ngOnDestroy(): void {
    environment.production || console.log('*** LayoutService constructor ngOnDestroy: ', JSON.stringify(this.config()));
    this.configUpdate.complete(); 
    this.overlayOpen.complete();
    this.topbarMenuOpen.complete();
    this.menuProfileOpen.complete();
  }

  updateStyle(config: AppConfig) {
    environment.production || console.log('*** LayoutService on updateStyle: ', JSON.stringify(config));
    environment.production || console.log('*** LayoutService on updateStyle: ', JSON.stringify(this._config));
    return (
      config.componentTheme !== this._config.componentTheme ||
      config.colorScheme !== this._config.colorScheme ||
      config.menuTheme !== this._config.menuTheme ||
      config.topbarTheme !== this._config.topbarTheme ||
      config.menuMode !== this._config.menuMode ||
      config.menuProfilePosition !== this._config.menuProfilePosition
    );
  }

  changeTheme() {
    environment.production || console.log('*** LayoutService on changeTheme: ', JSON.stringify(this.config()));

    let { colorScheme, componentTheme } = this.config();
    const themeLink = <HTMLLinkElement>(
      document.getElementById('theme-link')
    );
    const themeLinkHref = themeLink.getAttribute('href')!;
    const newHref = themeLinkHref
      .split('/')
      .map((el) =>
        el == this._config.componentTheme
          ? (el = componentTheme)
          : el == `theme-${this._config.colorScheme}`
            ? (el = `theme-${colorScheme}`)
            : el
      )
      .join('/');
    this.replaceThemeLink(newHref);
  }

  replaceThemeLink(href: string) {
    const id = 'theme-link';
    let themeLink = <HTMLLinkElement>document.getElementById(id);
    const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');

    themeLink.parentNode!.insertBefore(
      cloneLinkElement,
      themeLink.nextSibling
    );
    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
    });
  }

  changeScale(value: number) {
    document.documentElement.style.fontSize = `${value}px`;
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;

      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive =
        !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive =
        !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  onTopbarMenuToggle() {
    this.state.topbarMenuActive = !this.state.topbarMenuActive;
    if (this.state.topbarMenuActive) {
      this.topbarMenuOpen.next(null);
    }
  }

  onOverlaySubmenuOpen() {
    this.overlayOpen.next(null);
  }

  showConfigSidebar() {
    this.state.configSidebarVisible = true;
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isSlim() {
    return this.config().menuMode === 'slim';
  }

  isSlimPlus() {
    return this.config().menuMode === 'slim-plus';
  }

  isHorizontal() {
    return this.config().menuMode === 'horizontal';
  }

  isMobile() {
    return !this.isDesktop();
  }

  isRightMenuActive(): boolean {
    return this.state.rightMenuActive;
  }

  openRightSidebar(): void {
    this.state.rightMenuActive = true;
  }

  onMenuProfileToggle() {
    this.state.menuProfileActive = !this.state.menuProfileActive;
    if (
      this.state.menuProfileActive &&
      this.isHorizontal() &&
      this.isDesktop()
    ) {
      this.menuProfileOpen.next(null);
    }
  }
}
